import { useTheme } from "../../../CommonComponents/ThemeProvider";
import React, { useState, useEffect } from "react";
import QuickCashPanel from "./QuickCashPanel";
import PayButtonPanel from "./PayButtonPanel";
import { formatPrice } from "../../utils/FormatPrice";

export default function CashPaymentPanel({ total, cart, onPay }) {
    const { currentTheme } = useTheme();
    const [amountPaid, setAmountPaid] = useState("");
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState("");
    const QUICK_CASH_VALUES = [
        { label: "Exact", value: total, tooltip: `Use exact total ${formatPrice(total)}` },
        20,
        50,
        100,
        200,
        500,
        1000,
    ];

    useEffect(() => {
        setAmountPaid("");
        setDisplayValue("");
        setError("");
    }, [total]);

    const numericPaid = Number(amountPaid) || 0;
    const change = Math.max(numericPaid - total, 0);

    // Format number with commas and decimals
    const formatDisplayValue = (num) => {
        if (!num) return "";
        return new Intl.NumberFormat("en-PH", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(num);
    };

    const handleQuickCash = (value) => {
        const numValue = value.toString();
        setAmountPaid(numValue);
        setDisplayValue(formatDisplayValue(Number(numValue)));
        setError("");
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;

        // Remove all non-numeric characters except decimal point
        const cleanValue = inputValue.replace(/[^\d.]/g, "");

        // Prevent multiple decimals
        const parts = cleanValue.split(".");
        if (parts.length > 2) return;

        // Validate it's a valid number
        if (cleanValue === "" || cleanValue === ".") {
            setAmountPaid("");
            setDisplayValue("");
            setError("");
            return;
        }

        const numValue = parseFloat(cleanValue);

        // Prevent negative numbers
        if (numValue < 0) return;

        setAmountPaid(cleanValue);
        setDisplayValue(formatDisplayValue(numValue));
        setError("");
    };

    const handlePayClick = () => {
        if (cart.length === 0) {
            setError("Cart is empty");
            return;
        }
        if (numericPaid < total) {
            setError("Insufficient amount");
            return;
        }
        onPay({ paymentMethod: "cash", amountPaid: numericPaid });
    };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-slate-500">Quick Cash</p>
                    <p className="text-xs font-semibold text-slate-500">Due {formatPrice(total)}</p>
                </div>
                <QuickCashPanel values={QUICK_CASH_VALUES} onSelect={handleQuickCash} />
                <input
                    type="text"
                    inputMode="decimal"
                    value={displayValue}
                    onChange={handleInputChange}
                    placeholder="Enter amount received"
                    className="box-border h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-900 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                    style={{
                        boxShadow: undefined,
                        borderColor: undefined,
                        outline: undefined,
                        outlineColor: undefined,
                    }}
                    onFocus={e => e.target.style.boxShadow = `0 0 0 2px ${currentTheme.color}`}
                    onBlur={e => e.target.style.boxShadow = ''}
                />
                <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-medium text-slate-500">Change</span>
                    <span className="font-bold text-slate-900">{formatPrice(change)}</span>
                </div>
                {error && <p className="text-red-600 text-xs">{error}</p>}
                <PayButtonPanel
                    disabled={cart.length === 0 || numericPaid < total}
                    onClick={handlePayClick}
                >
                    Place Order
                </PayButtonPanel>
            </div>
        </div>
    );
}
